import { InfoTooltip } from "./InfoTooltip";
import type { ProgramByMeetingType } from "@/types/dashboard";

const MEETING_TYPE_COLORS: Record<string, string> = {
  client: "#90b5d8",
  learning: "#7ba57b",
  external: "#c1c1c1",
  general: "#274060"
};

const LEGEND = [
  { label: "Client", key: "client" },
  { label: "Learning", key: "learning" },
  { label: "External", key: "external" },
  { label: "General", key: "general" }
];

interface BarChartBoxProps {
  title: string;
  tooltipText: string;
  items: { meetingType: string; value: number; displayValue: string }[];
}

function BarChartBox({ title, tooltipText, items }: BarChartBoxProps) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const hasData = total > 0 && items.length > 0;

  return (
    <div className="chart-box">
      <header className="box-header">
        <div className="box-title">
          <span>{title}</span>
        </div>
        <InfoTooltip text={tooltipText} />
      </header>

      {hasData ? (
        <div
          className="bar-chart"
          role="img"
          aria-label={`${title} bar chart`}
        >
          {items.map((item) => {
            const pct = (item.value / total) * 100;
            const color = MEETING_TYPE_COLORS[item.meetingType.toLowerCase()] ?? "#94a3b8";
            return (
              <div
                key={item.meetingType}
                className="bar group/bar"
                style={{ width: `${pct}%`, backgroundColor: color }}
              >
                <span>{item.displayValue}</span>
                <span
                  role="tooltip"
                  className="bar-tooltip pointer-events-none"
                >
                  {item.meetingType} | {item.displayValue}
                  <span className="bar-tooltip-arrow" />
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="bar-chart bar-chart-empty"
          role="img"
          aria-label={`${title} bar chart with no data`}
        >
          <div className="bar bar-empty" style={{ width: "100%" }}>
            <span>&nbsp;</span>
          </div>
        </div>
      )}

      <div className="legend">
        {LEGEND.map((leg) => (
          <div key={leg.key} className="legend-item">
            <span
              className={`legend-color ${leg.key}`}
              aria-hidden="true"
            />
            <span>{leg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface MeetingTypeBarChartsProps {
  data: ProgramByMeetingType[];
}

export function MeetingTypeBarCharts({ data }: MeetingTypeBarChartsProps) {
  return (
    <div className="charts-row">
      <BarChartBox
        title="Total # of Programs by Meeting Type"
        tooltipText="Breakdown of programs by meeting type (e.g., Client, Learning, External)."
        items={data.map((d) => ({
          meetingType: d.meetingType,
          value: d.totalPrograms,
          displayValue: d.totalProgramsString
        }))}
      />
      <BarChartBox
        title="Total Room Nights by Meeting Type"
        tooltipText="Number of room nights associated with each meeting type."
        items={data.map((d) => ({
          meetingType: d.meetingType,
          value: d.totalRoomNights,
          displayValue: d.totalRoomNightsString
        }))}
      />
      <BarChartBox
        title="DU Capacity by Meeting Type"
        tooltipText="Proportion of DU's total capacity used by each meeting type."
        items={data.map((d) => ({
          meetingType: d.meetingType,
          value: d.duCapByMeetingType,
          displayValue: `${d.duCapByMeetingType}%`
        }))}
      />
    </div>
  );
}
